import React from 'react';
import {
  Card, CardContent, CardActions, Box, Typography, Avatar,
  Chip, Rating, Button, Stack, Tooltip, Badge
} from '@mui/material';
import {
  LocationOn, Verified, Star, TrendingUp, AutoAwesome,
  WorkHistory, CheckCircle
} from '@mui/icons-material';

import { brand, border } from '../../theme/colors';

const VerifierCard = ({ verifier, onHireClick, isAIRecommended = false, aiMatchScore = null, aiReasons = [] }) => {
  const {
    user_id,
    name,
    profile_image_url,
    tier,
    specializations = [],
    years_experience,
    base_city,
    avg_rating,
    review_count,
    price_range_min,
    price_range_max,
    bio,
    is_available,
    verification_status,
    has_worked_together
  } = verifier;

  const getTierColor = (tier) => {
    switch (tier?.toLowerCase()) {
      case 'professional':
        return brand.primary;
      case 'general':
        return '#78909c';
      default:
        return '#9e9e9e';
    }
  };

  const getTierIcon = (tier) => {
    if (tier?.toLowerCase() === 'professional') {
      return <Verified sx={{ fontSize: 16, ml: 0.5 }} />;
    }
    return null;
  };

  return (
    <Card
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.3s cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        border: isAIRecommended ? `2px solid ${brand.primary}` : `1px solid ${border.default}`,
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: 4
        }
      }}
    >
      {/* AI Recommended Badge */}
      {isAIRecommended && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 12,
            zIndex: 1
          }}
        >
          <Tooltip title={`AI Match Score: ${Math.round((aiMatchScore || 0) * 100)}%`}>
            <Chip
              icon={<AutoAwesome sx={{ fontSize: 16 }} />}
              label={`${Math.round((aiMatchScore || 0) * 100)}% Match`}
              size="small"
              sx={{
                background: brand.gradient,
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          </Tooltip>
        </Box>
      )}

      {/* Previous Work Badge */}
      {has_worked_together && (
        <Box
          sx={{
            position: 'absolute',
            top: isAIRecommended ? 48 : 12,
            right: 12,
            zIndex: 1
          }}
        >
          <Tooltip title="You've worked with this verifier before">
            <Chip
              icon={<WorkHistory sx={{ fontSize: 16 }} />}
              label="Previous"
              size="small"
              sx={{
                backgroundColor: '#4caf50',
                color: 'white',
                fontWeight: 600,
                '& .MuiChip-icon': {
                  color: 'white'
                }
              }}
            />
          </Tooltip>
        </Box>
      )}

      <CardContent sx={{ flexGrow: 1, pb: 1 }}>
        {/* Header */}
        <Box display="flex" alignItems="flex-start" mb={2}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              is_available ? (
                <CheckCircle sx={{ fontSize: 20, color: '#4caf50', backgroundColor: 'white', borderRadius: '50%' }} />
              ) : null
            }
          >
            <Avatar
              src={profile_image_url}
              alt={name}
              sx={{
                width: 64,
                height: 64,
                mr: 2,
                border: '3px solid #f5f5f5'
              }}
            >
              {name?.charAt(0)}
            </Avatar>
          </Badge>

          <Box flexGrow={1}>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              {name}
            </Typography>

            {/* Tier Badge */}
            <Chip
              label={tier?.toUpperCase() || 'STANDARD'}
              icon={getTierIcon(tier)}
              size="small"
              sx={{
                backgroundColor: getTierColor(tier),
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.75rem',
                height: 24,
                '& .MuiChip-icon': {
                  color: '#fff'
                }
              }}
            />

            {verification_status === 'verified' && (
              <Chip
                icon={<Verified sx={{ fontSize: 14 }} />}
                label="Verified"
                size="small"
                sx={{
                  ml: 1,
                  backgroundColor: '#2196f3',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  height: 24,
                  '& .MuiChip-icon': {
                    color: 'white'
                  }
                }}
              />
            )}
          </Box>
        </Box>

        {/* Rating */}
        {avg_rating > 0 && (
          <Box display="flex" alignItems="center" mb={1.5}>
            <Rating value={parseFloat(avg_rating)} precision={0.1} size="small" readOnly />
            <Typography variant="body2" color="text.secondary" ml={1}>
              {parseFloat(avg_rating).toFixed(1)} ({review_count || 0} {review_count === 1 ? 'review' : 'reviews'})
            </Typography>
          </Box>
        )}

        {/* Location and Experience */}
        <Stack direction="row" spacing={2} mb={1.5}>
          {base_city && (
            <Box display="flex" alignItems="center">
              <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.5 }} />
              <Typography variant="body2" color="text.secondary">
                {base_city}
              </Typography>
            </Box>
          )}
          {years_experience > 0 && (
            <Typography variant="body2" color="text.secondary">
              {years_experience} {years_experience === 1 ? 'year' : 'years'} exp.
            </Typography>
          )}
        </Stack>

        {/* Specializations */}
        {specializations && specializations.length > 0 && (
          <Box mb={1.5}>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {specializations.slice(0, 3).map((spec, index) => (
                <Chip
                  key={index}
                  label={spec}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderColor: brand.primary,
                    color: brand.primary,
                    fontWeight: 500
                  }}
                />
              ))}
              {specializations.length > 3 && (
                <Chip
                  label={`+${specializations.length - 3} more`}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.75rem',
                    height: 24,
                    borderColor: '#999',
                    color: '#999',
                    fontWeight: 500
                  }}
                />
              )}
            </Stack>
          </Box>
        )}

        {/* Bio */}
        {bio && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              mb: 1.5,
              display: '-webkit-box',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
              lineHeight: 1.6
            }}
          >
            {bio}
          </Typography>
        )}

        {/* AI Reasons */}
        {isAIRecommended && aiReasons && aiReasons.length > 0 && (
          <Box
            sx={{
              backgroundColor: 'rgba(13, 148, 136, 0.06)',
              borderRadius: 1,
              p: 1.5,
              mb: 1.5,
              border: '1px solid rgba(13, 148, 136, 0.15)'
            }}
          >
            <Stack direction="row" spacing={0.5} alignItems="center" mb={0.5}>
              <TrendingUp sx={{ fontSize: 16, color: brand.primary }} />
              <Typography variant="caption" fontWeight={600} color={brand.primary}>
                Why AI Recommends:
              </Typography>
            </Stack>
            {aiReasons.slice(0, 2).map((reason, index) => (
              <Typography
                key={index}
                variant="caption"
                display="block"
                color="text.secondary"
                sx={{ lineHeight: 1.4, mb: 0.3 }}
              >
                • {reason}
              </Typography>
            ))}
          </Box>
        )}

        {/* Price Range */}
        {price_range_min && price_range_max && (
          <Typography variant="body2" fontWeight={600} color="text.primary">
            ${price_range_min.toLocaleString()} - ${price_range_max.toLocaleString()}
          </Typography>
        )}
      </CardContent>

      <CardActions sx={{ px: 2, pb: 2, pt: 0 }}>
        <Button
          fullWidth
          variant="contained"
          onClick={() => onHireClick(verifier)}
          disabled={!is_available}
          sx={{
            background: is_available
              ? brand.gradient
              : '#ccc',
            color: 'white',
            textTransform: 'none',
            fontWeight: 600,
            py: 1,
            '&:hover': {
              background: is_available
                ? brand.gradientHover
                : '#ccc'
            }
          }}
        >
          {is_available ? 'Hire Verifier' : 'Not Available'}
        </Button>
      </CardActions>
    </Card>
  );
};

export default VerifierCard;
